/**
 * useSparksRoleSync - Handles role-based sparks grants only.
 * - Student (first time): grants signup bonus
 * - Lost student status: handled server-side by admin via on_student_status_lost
 *
 * Daily visitor grants are NOT handled here anymore — they live in
 * useUserActivity (global, fires on any authenticated route).
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';
import { grantStudentBonus } from '@/services/sparks';
import { useQueryClient } from '@tanstack/react-query';

export function useSparksRoleSync() {
  const { user } = useAuth();
  const { effectiveRole } = useRole();
  const queryClient = useQueryClient();
  const lastRole = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    const prevRole = lastRole.current;
    lastRole.current = effectiveRole;

    const roleChanged = prevRole !== null && prevRole !== effectiveRole;

    const sync = async () => {
      try {
        if (effectiveRole === 'student' && (prevRole === null || roleChanged)) {
          await grantStudentBonus(user.id);
          queryClient.invalidateQueries({ queryKey: ['sparks-wallet', user.id] });
        }
      } catch (err) {
        console.error('[useSparksRoleSync] Error:', err);
      }
    };

    sync();
  }, [user?.id, effectiveRole, queryClient]);
}
